import React, { useEffect, useLayoutEffect, useState, useContext, useRef } from 'react';
import { AppContext } from '../../context/AppContext.jsx';
import axios from 'axios';
import { useLanguage } from "../../i18n/LanguageContext.jsx";
import { getFirstImage, slugify, getSlug } from '../../helpers/HelperFunctions.js';
import i18n from "../../i18n/i18n.js";
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import ScrollTrigger from '../../../gsap/ScrollTrigger.min.js';
gsap.registerPlugin(ScrollTrigger);

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';

const NewsHpTemplate = ({ itemId, pageTitle }) => {
    const { language } = useLanguage();
    const [page, setPage] = useState(null);
    const [articles, setArticles] = useState(null);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const { siteMap } = useContext(AppContext);

    const nextButtonRef = useRef(null);
    const prevButtonRef = useRef(null);
    const swiperRef = useRef(null);

    const pageUrl = `/${language}${process.env.REACT_APP_API_PAGE_URL}`;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${pageUrl}/${itemId}`);
                setPage(res.data);
                console.log("Progetti Home Page", res.data);
            } catch (error) {
                console.error('Errore durante il fetchla pagina:', error);
            }
        };
        fetchData();
    }, [pageUrl, itemId]);

    useEffect(() => {
        if (page && siteMap) {
            setComponentLoaded(true);
        } else {
            setComponentLoaded(false);
        }
    }, [page, siteMap]);

    useEffect(() => {
        if (page && page.articles) {
            setArticles(page.articles);
        }
    }, [page]);

    // Impostiamo la navigazione manualmente quando gli items sono pronti
    useEffect(() => {
        if (page && siteMap && articles && articles.items) {
            const swiperInstance = swiperRef.current?.swiper;
            if (swiperInstance && nextButtonRef.current && prevButtonRef.current) {
                swiperInstance.params.navigation.nextEl = nextButtonRef.current;
                swiperInstance.params.navigation.prevEl = prevButtonRef.current;
                swiperInstance.navigation.init();
                swiperInstance.navigation.update();
            }
        }
    }, [articles, page, siteMap]);

    const getFirstWord = (text) => {
        if (!text) return '';
        const words = text.trim().split(' ');
        return words.length > 0 ? words[0] : '';
    };

    const getRemainingWords = (text) => {
        if (!text) return '';
        const words = text.trim().split(' ');
        return words.length > 1 ? words.slice(1).join(' ') : '';
    };

    useEffect(() => {
        if (!componentLoaded || !articles?.items) return;
        const magicHr = document.querySelector('.magic-hr');

        gsap.from(magicHr, {
            scrollTrigger: {
                trigger: magicHr,
                start: "top bottom-=200px",
                toggleActions: "play none none none",
                id: `magic-hr`,
                /*markers: true,*/
            },
            x: "-120%",
            opacity: 0,
            duration: 2,
            ease: 'power2.out',
        });

        return () => {
            ScrollTrigger.getById(`magic-hr`)?.kill();
        };
    }, [componentLoaded, articles]);

    return (
        <>
            {page && siteMap && (
                <div className="container-xl">
                    <div className="row justify-content-between align-items-center position-relative">
                        <div className="col-12">
                            <h2 className="bg-white br-lg d-inline-block fs-1 lh-1 mb-0 pe-4 pe-md-10">
                                <span className="fw-bold">{getFirstWord(page.title)}</span>
                                <span className="fw-light"> {getRemainingWords(page.title)}</span>
                            </h2>
                        </div>
                        <div className="col-12 align-items-center overflow-hidden d-flex justify-content-center position-absolute py-2 z-n1">
                            <hr className="mb-0 mt-0 opacity-100 magic-hr w-100" />
                        </div>
                    </div>
                    <div className="row mt-5 mt-lg-10">
                        <div className="col-12 position-relative" id="newsHpCarousel-wrapper">
                            <Swiper
                                ref={swiperRef}
                                modules={[Navigation]}
                                spaceBetween={30}
                                slidesPerView={1}
                                loop={true}
                                centeredSlides={false}
                                id="newsHpCarousel"
                                navigation={{
                                    nextEl: '.swiper-button-next-news',
                                    prevEl: '.swiper-button-prev-news'
                                }}
                                breakpoints={{
                                    576: {
                                        slidesPerView: 2,
                                        spaceBetween: 20
                                    },
                                    992: {
                                        slidesPerView: 4,
                                        spaceBetween: 30
                                    }
                                }}
                            >
                                {articles?.items && articles.items.map((article, index) => (
                                    <SwiperSlide key={article.id || index}>
                                        <Link to={`${siteMap['ceab2ab8-c841-485a-8b03-502bfed9ab2b'].path}/${getSlug(article)}`} className="text-decoration-none text-dark d-block">
                                            <div className="news-card">
                                                {article.media && article.media.length > 0 && (
                                                    <div className="news-card-img mb-3">
                                                        <img
                                                            src={article.media[0].urlLarge}
                                                            alt={article.title}
                                                            className="w-100 h-auto object-fit-cover"
                                                            style={{ aspectRatio: '2.8 / 3.5' }}
                                                        />
                                                    </div>
                                                )}
                                                <div className="news-card-content">
                                                    <h3 className="fs-5 lh-base mb-0 font-secondary fw-bold pe-4">
                                                        {article.title}
                                                    </h3>
                                                    {article.tagNames && article.tagNames.length > 0 && (
                                                        <p className="text-muted mb-3 small font-secondary fw-light fs-6">
                                                            {article.tagNames.map((tag, idx) => (
                                                                <span key={idx}>#{tag}{idx < article.tagNames.length - 1 ? ' ' : ''}</span>
                                                            ))}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </Link>
                                    </SwiperSlide>
                                ))}
                            </Swiper>

                            {articles && articles.items && articles.items.length > 1 && (
                                <div className="navigation-wrapper d-flex justify-content-center align-items-center mt-5">
                                    <div 
                                        ref={prevButtonRef} 
                                        className="swiper-button-prev-news cursor-pointer"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <svg width="46" height="89" viewBox="0 0 46 89" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M44.7072 0.349609L0.707153 45.3496L44.7072 88.3496" stroke="black"/>
                                            <path d="M44.7072 0.349609L0.707153 45.3496L44.7072 88.3496" stroke="black"/>
                                        </svg>
                                    </div>
                                    <span className="mx-5 text-uppercase font-secondary fw-light tracking-widest">Scroll</span>
                                    <div 
                                        ref={nextButtonRef} 
                                        className="swiper-button-next-news cursor-pointer"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <svg width="46" height="89" viewBox="0 0 46 89" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0.357544 0.349609L44.3575 45.3496L0.357544 88.3496" stroke="black"/>
                                            <path d="M0.357544 0.349609L44.3575 45.3496L0.357544 88.3496" stroke="black"/>
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NewsHpTemplate; 